import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { CloseIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    CatalogItemPriceRow,
    CatalogItemPricesTableCallbacks,
    CatalogItemPricesViewOptions,
    getKeepPriceDisabledReason,
    getPriceActionAvailability,
    getSetDefaultDisabledReason,
} from "../catalogItemPrices.types";

export const PriceActionsCell = memo(function PriceActionsCell({
    row,
    table,
}: CellContext<ViewTableFeatures, CatalogItemPriceRow, unknown>) {
    const { callbacks, options } = useViewTableMeta<
        CatalogItemPricesTableCallbacks,
        CatalogItemPricesViewOptions
    >(table);
    const price = row.original;
    if (price.isDraft) {
        return (
            <ViewTableCell captureClicks>
                <Button
                    type="button"
                    variant="secondary"
                    size="xs"
                    iconOnly
                    aria-label="Cancel"
                    disabled={options?.isDraftPending}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={callbacks?.onCancelDraft}
                >
                    <CloseIcon />
                </Button>
            </ViewTableCell>
        );
    }
    if (price.isStaged) {
        return (
            <ViewTableCell captureClicks>
                <Button
                    type="button"
                    variant="secondary"
                    size="xs"
                    iconOnly
                    aria-label="Remove"
                    disabled={options?.isDraftPending}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => callbacks?.onRemoveStaged(price.id)}
                >
                    <CloseIcon />
                </Button>
            </ViewTableCell>
        );
    }
    const availability = getPriceActionAvailability(price);

    return (
        <ViewTableCell captureClicks>
            <Menu.More className={viewTableStyles.showOnHover}>
                <Menu.Item
                    disabled={!availability.canSetDefault}
                    disabledReason={getSetDefaultDisabledReason(price)}
                    onClick={() => callbacks?.onSetDefault(price)}
                >
                    Set as default
                </Menu.Item>
                {price.active ? (
                    <Menu.Item
                        disabled={!availability.canArchive}
                        disabledReason={getKeepPriceDisabledReason(price)}
                        onClick={() => callbacks?.onArchiveToggle(price)}
                    >
                        Archive
                    </Menu.Item>
                ) : (
                    <Menu.Item
                        onClick={() => callbacks?.onArchiveToggle(price)}
                    >
                        Unarchive
                    </Menu.Item>
                )}
                <Menu.Item
                    destructive
                    disabled={!availability.canDelete}
                    disabledReason={getKeepPriceDisabledReason(price)}
                    onClick={() => callbacks?.onDelete(price)}
                >
                    Delete
                </Menu.Item>
            </Menu.More>
        </ViewTableCell>
    );
});
